/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

/*
  Things to know about this feature:

  1. Every time there is an update to the planwall view, the case ypes.CHECK_SAVED_VIEWS_FOR_CURRENT_VIEW
  gets triggered. The purpose of this is to activate a saved view in the case that the user
  configures their wall to match a saved view.

  2. In the componentDidUpdate function of PlanWall/index.jsx, you will see logic pertaining
  to how the state here is parsed and stored at the component level. This could potentally be streamlined
  such that the component heirarchy for the planwall deals directly with this reducer. 


  3. When the user navigates to different levels of the planwall, or if they leave the planwall, 
  their configuration data here get stored on the their User instance on the backend. This data is 
  loaded in to the reducer when the app loads. 

  4. The right panel list of sprints and targets has its own state. The state here is not persisted on the backend. 

  5. The kanbanWallState value is linked to the dropdown target and owner filters on the kanban and cardwall. This state value
  is persisted on the user object with everything else, and api calls are made to persist the value when navigating between the
  planwall, cardwall, or kanbanwall

 */

import { isEqual, isEmpty, cloneDeep } from 'lodash';
import Types from '../constants/actionTypes';

const defaultView = localStorage.activeView
  ? JSON.parse(localStorage.activeView)
  : {};

const filterHash =
  'eyJzb3J0RGVzY2VuZGluZyI6ZmFsc2UsInNvcnRGaWVsZCI6InJhbmsiLCJ2cnNGaWx0ZXIiOnsicmlzayI6W10sInZhbHVlIjpbXSwic2l6ZSI6W10sInJyb2UiOltdLCJ0YyI6W119LCJzcHJpbnRGaWx0ZXIiOltdLCJ0YXJnZXRGaWx0ZXIiOltdLCJzdGF0ZUZpbHRlciI6W10sImxpc3RUZWFtRmlsdGVyIjpbXSwicGFyZW50RmlsdGVyIjpbXSwib3duZXJGaWx0ZXIiOltdLCJjcmVhdG9yRmlsdGVyIjpbXSwidGVhbUZpbHRlciI6W10sImJsb2NrZWRGaWx0ZXIiOi0xLCJ0eXBlRmlsdGVyIjpbXSwibGlzdFByb2R1Y3RGaWx0ZXIiOltdfQ==';
const generateDefaultPlanwallState = () => {
  const levelsArr = [1, 2, 3, 4, 5];
  const stateBoilerplate = {
    col: null,
    sort: 'cmFuayxmYWxzZQ==',
    fil: filterHash,
    sc: false,
    st: false,
    sTeam: false,
    eTeam: 'W10=',
    mod: '',
    lcol: false,
    rcol: false
  };
  const defaultState = levelsArr.map((level) => ({
    level,
    state: { ...stateBoilerplate }
  }));
  return defaultState;
};

const generateDefaultPreviousStates = () => {
  const levelsArr = [1, 2, 3, 4, 5];
  const defaultObject = {};
  levelsArr.forEach((level) => {
    defaultObject[level] = {
      col: null,
      sort: 'cmFuayxmYWxzZQ==',
      fil: filterHash,
      sc: false,
      st: false,
      sTeam: false,
      eTeam: 'W10=',
      mod: '',
      lcol: false,
      rcol: false
    };
  });
  return defaultObject;
};

const generateRightPanelRowSprintState = () => {
  const levelsArr = [1, 2, 3, 4, 5];
  const stateBoilerplate = {
    rstate: 'e30=',
    open: ''
  };

  const defaultState = levelsArr.map((level) => ({
    level,
    state: { ...stateBoilerplate }
  }));
  return defaultState;
};

const initialKanbanWallState = { selectedTargets: [], selectedUsers: [] };

const initialState = {
  savedViews: [],
  activeView: defaultView,
  planwallStates: generateDefaultPlanwallState(),
  previousStates: generateDefaultPreviousStates(),
  sprintPanelRowStates: generateRightPanelRowSprintState(),
  kanbanWallState: cloneDeep(initialKanbanWallState),
  savedKanbanFilters: [],
  isFetching: false
};

const savedViewReducer = (state = initialState, action) => {
  let view;
  let updatedPlanwallStates;
  let currentLevel;
  let currentSpace;
  let viewMatchWhatsSaved;
  let newPrevState;
  let updatedPreviousStates = state.previousStates; // default
  let neededPrevState;
  let updatedSavedViews;
  let newKanbanStates;
  let savedKanbanFilter;

  switch (action.type) {
    case Types.SAVED_VIEW_FETCHING:
      return {
        ...state,
        isFetching: true
      };
    case Types.POPULATE_SAVED_VIEWS:
      return {
        ...state,
        savedViews: action.savedViews,
        previousStates: action.previousViews || state.previousStates
      };

    case Types.ACTIVATE_SAVED_VIEW:
      view = isEqual(action.view, state.activeView) ? {} : action.view;
      if (!isEmpty(view)) {
        window.localStorage.setItem('activeView', JSON.stringify(view));
      } else {
        window.localStorage.removeItem('activeView');
      }

      if (isEmpty(view)) {
        /*
          case: when the active view is being cleared.
          What happens:
          - take the prevState by the level - neededPrevState, 
          - replace the corresponding object in updatedPlanwallStates
            with the prevState (in the map function later on)
        */
        neededPrevState = state.previousStates[action.level];
      } else if (!isEmpty(view) && !isEmpty(state.activeView)) {
        /*
          case: when the active view is being replaced with another view.
          do not update previous
        */
      } else if (!isEmpty(view) && isEmpty(state.activeView)) {
        /*
          case: when the active view is being populated
         What happens?
          - grab the current view by the level from state.planwallStates (newPrevState), 
          - plug that into the prevView object
        */
        newPrevState = state.planwallStates.find(
          (pws) => pws.level === action.level
        );
        updatedPreviousStates = {
          ...state.previousStates,
          [action.level]: { ...newPrevState.state }
        };
      }

      updatedPlanwallStates = state.planwallStates.map((pws) => {
        // setting the active view
        if (pws.level === action.level && !neededPrevState) {
          return {
            ...pws,
            state: action.view.view
          };
        }
        // removing the active view
        if (pws.level === action.level && neededPrevState) {
          return {
            ...pws,
            state: neededPrevState
          };
        }
        return pws;
      });

      return {
        ...state,
        activeView: view,
        planwallStates: updatedPlanwallStates,
        previousStates: updatedPreviousStates
      };
    case Types.CLEAR_ACTIVE_VIEW:
      window.localStorage.removeItem('activeView');
      return {
        ...state,
        activeView: {}
      };
    case Types.CREATE_SAVED_VIEW:
      window.localStorage.setItem('activeView', JSON.stringify(action.newView));
      return {
        ...state,
        savedViews: [...state.savedViews, action.newView],
        activeView: action.newView,
        isFetching: false
      };

    case Types.DELETE_SAVED_VIEW:
      updatedSavedViews = state.savedViews.filter(
        (sv) => !isEqual(action.view, sv)
      );
      if (isEqual(state.activeView, action.view)) {
        neededPrevState = state.previousStates[action.view.level];

        updatedPlanwallStates = state.planwallStates.map((pws) => {
          if (pws.level === action.view.level && neededPrevState) {
            return {
              ...pws,
              state: neededPrevState
            };
          }
          return pws;
        });

        return {
          ...state,
          savedViews: updatedSavedViews,
          activeView: {},
          isFetching: false,
          planwallStates: updatedPlanwallStates
        };
      }
      return {
        ...state,
        savedViews: updatedSavedViews,
        isFetching: false
      };

    case Types.DELETE_PRODUCT:
      // eslint-disable-next-line no-case-declarations
      const { productId } = action;
      return {
        ...state,
        savedViews: state.savedViews.filter(
          (sv) => !(sv.space_id === productId && sv.space_type === 'product')
        )
      };

    case Types.DELETE_TEAM:
      // eslint-disable-next-line no-case-declarations
      const { team } = action;
      return {
        ...state,
        savedViews: state.savedViews.filter(
          (sv) => !(sv.space_id === team.id && sv.space_type === 'team')
        )
      };

    case Types.UPDATE_PLANWALL_STATE:
      updatedPlanwallStates = state.planwallStates.map((pws) => {
        if (pws.level === action.level) {
          return {
            level: pws.level,
            state: { ...action.state }
          };
        }
        return pws;
      });
      return {
        ...state,
        planwallStates: updatedPlanwallStates
      };

    case Types.POPULATE_PLANWALL_STATE:
      return {
        ...state,
        planwallStates: action.planwallDefaults
      };

    case Types.CLEAR_FILTERS_ON_SPACE_CHANGE:
      updatedPlanwallStates = state.planwallStates.map((pws) => ({
        ...pws,
        state: {
          ...pws.state,
          fil: 'e30='
        }
      }));
      return {
        ...state,
        planwallStates: updatedPlanwallStates
      };

    case Types.CHECK_SAVED_VIEWS_FOR_CURRENT_VIEW:
      currentSpace = JSON.parse(localStorage.getItem('selectedSpace'));
      currentLevel = state.planwallStates.find(
        (pws) => pws.level === action.level
      );

      viewMatchWhatsSaved = state.savedViews.find((view) => {
        return (
          currentLevel.state.col === view.view.col &&
          currentLevel.state.fil === view.view.fil &&
          currentLevel.state.sort === view.view.sort &&
          view.space_id === currentSpace.id
        );
      });

      if (viewMatchWhatsSaved && viewMatchWhatsSaved.level === action.level) {
        window.localStorage.setItem(
          'activeView',
          JSON.stringify(viewMatchWhatsSaved)
        );
        return {
          ...state,
          activeView: viewMatchWhatsSaved
        };
      }
      if (!isEmpty(state.activeView)) {
        return {
          ...state,
          activeView: {}
        };
      }

      return state;

    case Types.SWITCH_PRODUCT_TEAM_COLUMNS:
      updatedPlanwallStates = state.planwallStates.map((pws) => {
        if (pws.level === action.level) {
          return {
            ...pws,
            state: {
              ...pws.state,
              col: btoa(JSON.stringify(action.columnObject))
            }
          };
        }
        return pws;
      });

      return {
        ...state,
        planwallStates: updatedPlanwallStates
      };

    case Types.UPDATE_RIGHT_PANEL_STATE:
      updatedPlanwallStates = state.sprintPanelRowStates.map((pws) => {
        if (pws.level === action.level) {
          return {
            ...pws,
            state: {
              ...action.rowState
            }
          };
        }
        return pws;
      });

      return {
        ...state,
        sprintPanelRowStates: updatedPlanwallStates
      };

    case Types.POPULATE_KANBAN_FILTERS:
      return {
        ...state,
        kanbanWallState: action.kanbanFilters
      };
    case Types.ADD_KANBAN_FILTER:
      newKanbanStates = cloneDeep(state.kanbanWallState);
      newKanbanStates[action.filterName].push(action.value);
      return {
        ...state,
        kanbanWallState: newKanbanStates
      };
    case Types.REMOVE_KANBAN_FILTER:
      newKanbanStates = cloneDeep(state.kanbanWallState);
      newKanbanStates[action.filterName] = newKanbanStates[
        action.filterName
      ].filter((val) => val !== action.value);
      return {
        ...state,
        kanbanWallState: newKanbanStates
      };
    case Types.CLEAR_KANBAN_FILTER:
      newKanbanStates = cloneDeep(state.kanbanWallState);
      newKanbanStates[action.filterName] = [];
      return {
        ...state,
        kanbanWallState: newKanbanStates
      };
    case Types.RESET_KANBAN_FILTER:
      return {
        ...state,
        kanbanWallState: cloneDeep(initialKanbanWallState)
      };

    case Types.SET_SAVED_KANBAN_FILTERS:
      savedKanbanFilter = action.views.find(
        (view) =>
          view.space_id === action.selectedSpace.id &&
          view.space_type === action.selectedSpace.type
      );
      if (savedKanbanFilter && savedKanbanFilter.filter) {
        return {
          ...state,
          savedKanbanFilters: action.views,
          kanbanWallState: savedKanbanFilter.filter
        };
      }

      return {
        ...state,
        savedKanbanFilters: action.views,
        kanbanWallState: cloneDeep(initialKanbanWallState)
      };
    case Types.UPDATE_SAVED_KANBAN_FILTERS:
      return {
        ...state,
        savedKanbanFilters: action.views
      };

    case Types.CLEAR_ACTIVE_SPINNER:
      return { ...state, isFetching: false } 
    default:
      return state;
  }
};

export default savedViewReducer;
